import * as React from "react";
import { Button, Modal, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { CodeEditor } from "../../pages/group/identityMatcher/codeEditor";
import { ImportOutlined } from "@ant-design/icons";
import { InfoTooltip } from "../InfoTooltip";
import { useAppDispatch } from "../../store/hooks";
import { addNotification } from "../../store/notificationDrawerSlice";

interface Props {
  title: string;
  initialValue: string;
  open: boolean;
  onOk: (value: string) => void;
  onClose: () => void;
  readonly?: boolean;
  explanation?: React.ReactNode;
}

export const CodeEditorModal: React.FC<Props> = ({
  title,
  initialValue,
  open,
  onOk,
  onClose,
  readonly = false,
  explanation,
}) => {
  const dispatch = useAppDispatch();
  const page = typeof window !== "undefined" ? window.location.pathname : "";

  // States //
  const [value, setValue] = React.useState<string>(initialValue);
  const [file, setFile] = React.useState<RcFile>(null as any);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [isUploadDragOver, setIsUploadDragOver] = React.useState(false);
  const dragCounter = React.useRef(0);
  const uploadDragCounter = React.useRef(0);

  // Effects //
  React.useEffect(() => {
    if (file) {
      try {
        file.text().then((text) => {
          if (!text || text.trim().length === 0) {
            dispatch(
              addNotification({
                page,
                action: "Import",
                success: false,
                message: "File is empty",
              })
            );
            return;
          }
          setValue(text);
          dispatch(
            addNotification({
              page,
              action: "Import",
              success: true,
              message: "File imported successfully",
              details: { fileName: file.name },
            })
          );
        });
      } catch (error) {
        dispatch(
          addNotification({
            page,
            action: "Import",
            success: false,
            message: "Failed to load the imported file",
          })
        );
      }
    }
  }, [file]);

  function handleOnChange(value: string) {
    setValue(value);
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      try {
        const text = await file.text();
        if (!text || text.trim().length === 0) {
          dispatch(
            addNotification({
              page,
              action: "Import",
              success: false,
              message: "File is empty",
            })
          );
          return;
        }
        setValue(text);
        dispatch(
          addNotification({
            page,
            action: "Import",
            success: true,
            message: "File imported successfully",
            details: { fileName: file.name },
          })
        );
      } catch (error) {
        dispatch(
          addNotification({
            page,
            action: "Import",
            success: false,
            message: `Failed to read file: ${(error as Error).message}`,
          })
        );
      }
    }
  }

  return (
    <Modal
      className="codeEditorModal"
      width={"70%"}
      title={
        <div className="flex items-center justify-between">
          <div className="mr-4 text-lg">{title}</div>
          <div className="flex items-center h-6">
            <div className="flex gap-x-2">
              {readonly ? null : (
                <>
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      setFile(file);
                      return false;
                    }}
                    className="inline-block [&_.ant-upload]:block"
                  >
                    <div
                      className="flex items-center gap-2 px-3 py-1 border border-dashed rounded cursor-pointer transition-all text-sm"
                      style={{
                        borderColor: isUploadDragOver ? "var(--color-primary)" : "var(--color-primary)",
                        color: isUploadDragOver ? "var(--color-primary)" : "var(--color-primary)",
                        backgroundColor: isUploadDragOver ? "var(--color-primary-faint)" : "transparent",
                        opacity: isUploadDragOver ? 1 : 0.7,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        if (!isUploadDragOver) {
                          e.currentTarget.style.opacity = "0.7";
                        }
                      }}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        uploadDragCounter.current++;
                        setIsUploadDragOver(true);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDragLeave={() => {
                        uploadDragCounter.current--;
                        if (uploadDragCounter.current === 0) setIsUploadDragOver(false);
                      }}
                      onDrop={() => {
                        uploadDragCounter.current = 0;
                        setIsUploadDragOver(false);
                      }}
                    >
                      <ImportOutlined />
                      <span>Drop or click to import</span>
                    </div>
                  </Upload>
                  {explanation ? <InfoTooltip title={explanation} /> : null}
                </>
              )}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      maskClosable={false}
      footer={
        readonly ? (
          <div className="flex items-center justify-end gap-4">
            <Button type="primary" onClick={() => onClose()}>
              OK
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-4">
            <Button type="default" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => onOk(value)}>
              Set
            </Button>
          </div>
        )
      }
      destroyOnHidden={true}
    >
      <div
        className="relative rounded"
        style={{
          outline: isDragOver ? "2px dashed var(--color-primary)" : "none",
          outlineOffset: -2,
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragCounter.current++;
          setIsDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragCounter.current--;
          if (dragCounter.current === 0) {
            setIsDragOver(false);
          }
        }}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div
            className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none rounded"
            style={{ backgroundColor: "var(--color-primary-faint)" }}
          >
            <span style={{ color: "var(--color-primary)" }}>Drop file to import</span>
          </div>
        )}
        <CodeEditor
          delay
          value={value}
          setValue={handleOnChange}
          options={{ readOnly: readonly, basicSetup: { lineNumbers: true, autocompletion: false } }}
          language={"json"}
        />
      </div>
    </Modal>
  );
};
